import React, { useState, useEffect, useRef } from 'react';
import i18n, { getLang, setLang as setGlobalLang, t } from '../i18n';
import {
    BookOpenIcon,
    LightBulbIcon,
    AcademicCapIcon,
    ShieldCheckIcon,
    Bars3Icon,
    XMarkIcon,
    ArrowLeftIcon,
    DocumentTextIcon,
    UserGroupIcon,
    BoltIcon,
    ChartBarIcon,
    TicketIcon,
    BuildingOffice2Icon,
    GlobeAltIcon,
    CheckCircleIcon,
    ArrowRightIcon,
    ClipboardDocumentIcon,
} from '@heroicons/react/24/outline';
import { Link } from 'react-router-dom';

/* ─────────────────────────────────────────────────────────── */
/*  WIKI CONTENT DEFINITION                                    */
/* ─────────────────────────────────────────────────────────── */

const CATEGORIES = {
    fr: [
        { id: 'intro', label: 'Introduction', icon: BookOpenIcon },
        { id: 'producer', label: 'Producteur / Autoproducteur', icon: BuildingOffice2Icon },
        { id: 'onee', label: 'Opérateur Réseau (ONEE)', icon: BoltIcon },
        { id: 'vvb', label: 'Auditeur Indépendant (VVB)', icon: ShieldCheckIcon },
        { id: 'mitedd', label: 'Ministère (MTEDD / Admin)', icon: AcademicCapIcon },
        { id: 'certificates', label: 'Certificats & Transferts', icon: TicketIcon },
        { id: 'red2', label: 'Conformité RED II & Export EU', icon: GlobeAltIcon },
        { id: 'h2', label: 'Hydrogène Vert (Loi 82-21)', icon: LightBulbIcon },
        { id: 'verify', label: 'Vérification Publique (QR Code)', icon: CheckCircleIcon },
    ],
    en: [
        { id: 'intro', label: 'Introduction', icon: BookOpenIcon },
        { id: 'producer', label: 'Producer / Autoproducer', icon: BuildingOffice2Icon },
        { id: 'onee', label: 'Grid Operator (ONEE)', icon: BoltIcon },
        { id: 'vvb', label: 'Independent Auditor (VVB)', icon: ShieldCheckIcon },
        { id: 'mitedd', label: 'Ministry (MTEDD / Admin)', icon: AcademicCapIcon },
        { id: 'certificates', label: 'Certificates & Transfers', icon: TicketIcon },
        { id: 'red2', label: 'RED II Compliance & EU Export', icon: GlobeAltIcon },
        { id: 'h2', label: 'Green Hydrogen (Law 82-21)', icon: LightBulbIcon },
        { id: 'verify', label: 'Public Verification (QR Code)', icon: CheckCircleIcon },
    ],
};

const PAGES = {
    fr: {
        intro: {
            title: 'Bienvenue sur MAP GO',
            badge: 'Registre National',
            sections: [
                {
                    heading: 'Qu\'est-ce que MAP GO ?',
                    body: 'MAP GO est le registre national marocain de Garanties d\'Origine (GO), conforme aux normes EECS (European Energy Certificate System) et au Décret marocain 2.24.761. Il permet d\'émettre, de suivre, de transférer et d\'annuler des certificats d\'énergie renouvelable (solaire, éolien, hydrogène vert).',
                },
                {
                    heading: 'Les Rôles sur la Plateforme',
                    body: null,
                    table: {
                        headers: ['Rôle', 'Description', 'Pages Accessibles'],
                        rows: [
                            ['Producteur', 'Entreprise productrice d\'énergie renouvelable', 'Dashboard, Actifs, Certificats, Transferts'],
                            ['ONEE (Opérateur Réseau)', 'Vérifie les lectures de compteurs du réseau national', 'Portail de Comptage, Actifs'],
                            ['VVB (Auditeur Indépendant)', 'Vérifie les lignes directes et l\'autoproduction de tiers', 'Portail de Comptage, Actifs'],
                            ['MTEDD (Ministère)', 'Émet les certificats et supervise le registre', 'Toutes les pages + Approbations'],
                        ]
                    }
                },
                {
                    heading: 'Processus Global (du compteur au certificat)',
                    steps: [
                        'Inscription KYC de l\'entreprise productrice',
                        'Enregistrement et qualification de l\'actif (centrale)',
                        'Soumission des lectures de compteur par ONEE ou VVB',
                        'Validation et «Minting» souverain par le MTEDD',
                        'Certificat disponible dans le portefeuille du producteur',
                        'Transfert ou divulgation (annulation) selon l\'objectif',
                    ]
                }
            ]
        },
        producer: {
            title: 'Guide du Producteur',
            badge: 'Producteur / Autoproducteur',
            sections: [
                {
                    heading: 'Étape 1 — Inscription KYC (Page: /register)',
                    body: 'Accédez à la page d\'accueil et cliquez sur "Register Account". Remplissez le formulaire KYC avec les informations suivantes :',
                    fields: [
                        { name: 'Nom de l\'entreprise', hint: 'Ex: Noor Energy SA' },
                        { name: 'ICE / Numéro fiscal', hint: 'Ex: 001234567000045' },
                        { name: 'Représentant légal', hint: 'Nom complet avec signature électronique' },
                        { name: 'Email professionnel', hint: 'Sera utilisé pour la connexion' },
                        { name: 'Secteur d\'activité', hint: 'Ex: Production Solaire' },
                    ]
                },
                {
                    heading: 'Étape 2 — Enregistrement d\'Actif (Page: /devices/register)',
                    body: 'Depuis votre tableau de bord, cliquez sur "Plants" puis "Register New Asset". Remplissez le formulaire :',
                    fields: [
                        { name: 'Nom de la Centrale', hint: 'Ex: Noor Ouarzazate IV' },
                        { name: 'GSRN (18 chiffres GS1)', hint: 'Ex: 636999000000000001' },
                        { name: 'Type de Source', hint: 'Solaire, Éolien, Hydroélectrique' },
                        { name: 'Capacité Installée (MW)', hint: 'Ex: 50.00' },
                        { name: 'Ville / Région', hint: 'Ex: Ouarzazate, Souss-Massa' },
                        { name: 'Coordonnées GPS', hint: 'Latitude, Longitude (format WGS-84)' },
                        { name: 'Loi applicable', hint: 'Loi 13-09 (réseau) ou Loi 82-21 (autoproduction)' },
                        { name: 'Ligne directe ?', hint: 'Cocher si l\'actif est hors réseau national' },
                    ]
                },
                {
                    heading: 'Étape 3 — Suivi des Certificats (Page: /certificates)',
                    body: 'Après la vérification par ONEE/VVB et le Minting par le MTEDD, vos certificats apparaissent dans cet onglet. Vous pouvez :',
                    steps: [
                        'Télécharger le certificat PDF en cliquant sur l\'icône de téléchargement',
                        'Transférer un certificat à un autre participant via "Transférer"',
                        'Divulguer (annuler) un certificat pour preuve de consommation verte',
                    ]
                }
            ]
        },
        onee: {
            title: 'Guide ONEE — Opérateur Réseau',
            badge: 'ONEE / DSO Group',
            sections: [
                {
                    heading: 'Votre Rôle dans le Registre',
                    body: 'L\'ONEE est le «Trusted Verifier» pour toutes les centrales connectées au réseau national. Vous ne pouvez pas émettre de certificats directement — vous soumettez les données vérifiées, et le MTEDD finalise l\'émission.'
                },
                {
                    heading: 'Portail de Comptage (Page: /grid/metering)',
                    body: 'Connectez-vous avec vos identifiants ONEE et accédez au Portail de Comptage.',
                    steps: [
                        'La liste affiche les centrales dans votre réseau (ONEE tagged).',
                        'Cliquez sur une centrale pour ouvrir le formulaire de saisie.',
                        'Entrez la date de début et de fin de la période de production.',
                        'Saisissez le volume total mesuré en MWh.',
                        'Pour les projets en autoproduction connectés au réseau national (Loi 82-21), le formulaire affiche deux champs supplémentaires : "Self-Consumption (MWh)" et "Grid Injected (MWh)". La somme doit être égale au total.',
                        'Cliquez sur "Certify Measurement Readings" pour soumettre.',
                    ]
                },
                {
                    heading: 'Validation Automatique du Volume',
                    body: 'Le système valide automatiquement que : Consommation + Injection = Total. Si la somme ne correspond pas, la soumission est bloquée.',
                    callout: { type: 'warning', text: 'Attention : Une erreur de balance entraîne le rejet automatique de la demande de certification.' }
                }
            ]
        },
        vvb: {
            title: 'Guide VVB — Auditeur Indépendant',
            badge: 'ISO 14065 Accredited',
            sections: [
                {
                    heading: 'Périmètre d\'Intervention',
                    body: 'Les VVB (Voluntary Verification Body) sont activés pour les projets en Ligne Directe (hors réseau national) et pour les autoproducteurs qui choisissent un auditeur indépendant pour des raisons de confidentialité industrielle.'
                },
                {
                    heading: 'Actifs qui Vous Sont Assignés',
                    body: 'Sur la page Portail de Comptage, vous ne voyez que les actifs qui vous ont été explicitement assignés par le MTEDD. Si un actif est absent, contactez l\'administrateur pour l\'assignation.'
                },
                {
                    heading: 'Procédure de Vérification',
                    steps: [
                        'Consultez le rapport d\'audit de la centrale.',
                        'Accédez au Portail de Comptage et sélectionnez la centrale.',
                        'Saisissez les données vérifiées (identiques à la procédure ONEE).',
                        'Le sceau sur le certificat sera automatiquement «Vérifié par : Auditeur Indépendant (VVB)».',
                    ]
                }
            ]
        },
        mitedd: {
            title: 'Guide MTEDD — Autorité Souveraine',
            badge: 'Ministère / Admin',
            sections: [
                {
                    heading: 'Tableau de Bord Ministériel',
                    body: 'En tant qu\'Autorité Souveraine, vous avez accès à la vue «Command Center» du registre national : tous les actifs, tous les certificats, toutes les transactions — sans filtre.'
                },
                {
                    heading: 'File d\'Approbation (Page: /admin/approvals)',
                    body: 'Cette page est l\'interface centrale pour l\'émission souveraine. Elle affiche :',
                    steps: [
                        'Les demandes de certification soumises par ONEE/VVB (statut: PENDING_ISSUANCE).',
                        'Les demandes d\'enregistrement d\'actifs en attente de qualification.',
                        'Cliquez sur "Mint GO" pour créer le certificat cryptographique.',
                        'Cliquez sur "Qualify" pour approuver un actif de production.',
                    ]
                },
                {
                    heading: 'Journal d\'Audit (Page: /admin/audit)',
                    body: 'Toutes les actions dans le registre sont enregistrées de manière immuable. Le journal affiche : l\'acteur, le rôle, l\'action, l\'horodatage et le statut de chaque opération.',
                    callout: { type: 'info', text: 'Le journal d\'audit est la pièce maîtresse de la conformité réglementaire pour les inspections de l\'AIB et des organismes européens.' }
                }
            ]
        },
        certificates: {
            title: 'Certificats, Transferts & Divulgations',
            badge: 'Cycle de Vie du Certificat',
            sections: [
                {
                    heading: 'Structure d\'un Certificat EECS',
                    body: null,
                    table: {
                        headers: ['Champ', 'Valeur Exemple', 'Signification'],
                        rows: [
                            ['UUID', '4a81d3b3-...', 'Identifiant unique dans le registre'],
                            ['GS1 GIAI', '100-MA-0000000003', 'Identifiant GS1 standardisé (Maroc = MA)'],
                            ['Plage Sérielle', '1501 — 2500', 'Représente 1000 GOs (1 GO = 1 MWh)'],
                            ['Valeur Nominale', '1 MWh', 'Chaque certificat = 1 MWh'],
                            ['Catégorie', 'SELF_CONSUMPTION / GRID_INJECTED', 'Moteur Loi 82-21'],
                            ['Expiration RED II', '12 mois post-production', 'Verrou de transfert automatique'],
                        ]
                    }
                },
                {
                    heading: 'Effectuer un Transfert (Page: /transfers/new)',
                    fields: [
                        { name: 'Certificat Source', hint: 'Sélectionnez dans votre portefeuille' },
                        { name: 'Destinataire', hint: 'Email ou ID de l\'entité réceptrice' },
                        { name: 'Volume (MWh)', hint: 'Doit être ≤ volume disponible' },
                    ]
                },
                {
                    heading: 'Annuler / Divulguer (Page: /certificates)',
                    body: 'Cliquez sur "Annuler" sur un certificat pour lancer le processus de divulgation. Un PDF bilingue (FR/EN) sera généré automatiquement.',
                    callout: { type: 'warning', text: 'La divulgation est irréversible. Une fois annulé, le certificat ne peut plus être transféré.' }
                }
            ]
        },
        red2: {
            title: 'Conformité RED II & Export vers l\'UE',
            badge: 'Directive RED II (2018/2001)',
            sections: [
                {
                    heading: 'Règles de Validité Transactionnelle',
                    body: null,
                    table: {
                        headers: ['Règle', 'Délai', 'Conséquence'],
                        rows: [
                            ['Verrouillage de Transfert', '12 mois après fin production', 'Transfert automatiquement bloqué'],
                            ['Limite de Divulgation', '18 mois après fin production', 'Certificat expiré automatiquement'],
                            ['Signature Cryptographique', 'Au Minting', 'Hash SHA-256 immuable'],
                        ]
                    }
                },
                {
                    heading: 'Préparer une Exportation EU',
                    steps: [
                        'Assurez-vous que le certificat a moins de 12 mois.',
                        'Téléchargez le PDF du certificat (avec QR code et hash SHA-256).',
                        'L\'attestation mentionne l\'organisme vérificateur (ONEE ou VVB).',
                        'Soumettez le PDF à l\'organisme européen partenaire (AIB Hub).',
                    ]
                }
            ]
        },
        h2: {
            title: 'Hydrogène Vert — Loi 82-21',
            badge: 'Autoproduction H2',
            sections: [
                {
                    heading: 'Le Moteur de Séparation 800/200',
                    body: 'Pour un projet d\'hydrogène vert produisant 1000 MWh, la plateforme peut séparer automatiquement :'
                },
                {
                    heading: 'Exemple Complet',
                    body: null,
                    table: {
                        headers: ['Paramètre', 'Valeur', 'Badge Résultant'],
                        rows: [
                            ['Production Totale', '1000 MWh', '—'],
                            ['Self-Consumption H2', '800 MWh', 'Badge Indigo (SELF_CONSUMPTION)'],
                            ['Injection Réseau', '200 MWh', 'Badge Bleu (GRID_INJECTED)'],
                        ]
                    }
                },
                {
                    heading: 'Procédure de Saisie (ONEE / VVB)',
                    steps: [
                        'Sélectionnez la centrale H2 (elle doit être taguée "Autoproduction").',
                        'Entrez 1000 dans "Total Metered Output".',
                        'Le formulaire affiche automatiquement les champs "Self-Consumption" et "Grid Injected".',
                        'Entrez 800 et 200 respectivement.',
                        'Soumettez — deux certificats distincts sont créés.',
                    ],
                    callout: { type: 'info', text: 'Les certificats Self-Consumption sont réservés au reporting carbone de l\'hydrogène exporté. Les certificats Grid peuvent être vendus sur le marché national.' }
                }
            ]
        },
        verify: {
            title: 'Vérification Publique (QR Code)',
            badge: 'Transparent & Souverain',
            sections: [
                {
                    heading: 'Comment Scanner ?',
                    steps: [
                        'Ouvrez l\'appareil photo de votre smartphone.',
                        'Pointez-le sur le QR code du certificat PDF.',
                        'Accédez à "go.mapcure.com/verify/[UUID]".',
                        'L\'écran affiche le sceau de vérification vert avec les détails de l\'actif.',
                    ]
                },
                {
                    heading: 'Informations Affichées',
                    body: null,
                    table: {
                        headers: ['Information', 'Source'],
                        rows: [
                            ['Nom de la Centrale', 'Registre MAP GO'],
                            ['Organisme Vérificateur', 'ONEE ou VVB Accrédité'],
                            ['Catégorie', 'SELF_CONSUMPTION / GRID_INJECTED'],
                            ['Statut', 'ISSUED / CANCELLED / EXPIRED'],
                            ['Hash SHA-256', 'Signature Cryptographique'],
                        ]
                    }
                },
                {
                    heading: 'Aucun Compte Requis',
                    body: 'La vérification publique est accessible sans connexion, garantissant la transparence pour les acheteurs internationaux et les auditeurs européens.',
                    callout: { type: 'info', text: 'Ce portail est la «Fenêtre Souveraine» ouverte sur le marché mondial de l\'hydrogène vert.' }
                }
            ]
        }
    },
    en: {
        intro: {
            title: 'Welcome to MAP GO',
            badge: 'National Registry',
            sections: [
                {
                    heading: 'What is MAP GO?',
                    body: 'MAP GO is the Moroccan national registry for Guarantees of Origin (GO), compliant with EECS (European Energy Certificate System) standards and Moroccan Decree 2.24.761. It enables issuance, tracking, transfer, and cancellation of renewable energy certificates (solar, wind, green hydrogen).',
                },
                {
                    heading: 'Platform Roles',
                    body: null,
                    table: {
                        headers: ['Role', 'Description', 'Accessible Pages'],
                        rows: [
                            ['Producer', 'Renewable energy producing company', 'Dashboard, Assets, Certificates, Transfers'],
                            ['ONEE (Grid Operator)', 'Verifies meter readings on the national grid', 'Metering Portal, Assets'],
                            ['VVB (Independent Auditor)', 'Verifies direct lines and third-party autoproduction', 'Metering Portal, Assets'],
                            ['MTEDD (Ministry)', 'Issues certificates and oversees the registry', 'All pages + Approvals'],
                        ]
                    }
                },
                {
                    heading: 'End-to-End Process',
                    steps: [
                        'Producer company completes KYC registration',
                        'Asset (plant) registered and qualified',
                        'Meter readings submitted by ONEE or VVB',
                        'Sovereign validation and minting by MTEDD',
                        'Certificate appears in producer wallet',
                        'Transfer or disclosure (cancellation) based on objective',
                    ]
                }
            ]
        },
        producer: {
            title: 'Producer Guide',
            badge: 'Producer / Autoproducer',
            sections: [
                {
                    heading: 'Step 1 — KYC Registration (Page: /register)',
                    body: 'Go to the landing page and click "Register Account". Fill the KYC form with:',
                    fields: [
                        { name: 'Company Name', hint: 'Ex: Noor Energy SA' },
                        { name: 'Tax ID / ICE', hint: 'Ex: 001234567000045' },
                        { name: 'Legal Representative', hint: 'Full name with electronic signature' },
                        { name: 'Professional Email', hint: 'Will be used for login' },
                        { name: 'Business Sector', hint: 'Ex: Solar Production' },
                    ]
                },
                {
                    heading: 'Step 2 — Asset Registration (Page: /devices/register)',
                    body: 'From your dashboard, click "Plants" then "Register New Asset":',
                    fields: [
                        { name: 'Plant Name', hint: 'Ex: Noor Ouarzazate IV' },
                        { name: 'GSRN (18-digit GS1)', hint: 'Ex: 636999000000000001' },
                        { name: 'Source Type', hint: 'Solar, Wind, Hydro' },
                        { name: 'Installed Capacity (MW)', hint: 'Ex: 50.00' },
                        { name: 'City / Region', hint: 'Ex: Ouarzazate, Souss-Massa' },
                        { name: 'GPS Coordinates', hint: 'Latitude, Longitude (WGS-84 format)' },
                        { name: 'Applicable Law', hint: 'Law 13-09 (grid) or Law 82-21 (autoproduction)' },
                        { name: 'Direct Line?', hint: 'Check if the asset is off the national grid' },
                    ]
                },
                {
                    heading: 'Step 3 — Certificate Portfolio (Page: /certificates)',
                    body: 'After ONEE/VVB verification and MTEDD Minting, your certificates appear here. You can:',
                    steps: [
                        'Download the PDF certificate by clicking the download icon',
                        'Transfer a certificate to another participant via "Transfer"',
                        'Disclose (cancel) a certificate as proof of green consumption',
                    ]
                }
            ]
        },
        onee: {
            title: 'ONEE Guide — Grid Operator',
            badge: 'ONEE / DSO Group',
            sections: [
                {
                    heading: 'Your Role in the Registry',
                    body: 'ONEE is the "Trusted Verifier" for all plants connected to the national grid. You cannot issue certificates directly — you submit verified data, and the MTEDD finalizes issuance.'
                },
                {
                    heading: 'Metering Portal (Page: /grid/metering)',
                    body: 'Log in with your ONEE credentials and access the Metering Portal.',
                    steps: [
                        'The list shows plants in your network (ONEE tagged).',
                        'Click on a plant to open the data entry form.',
                        'Enter the production period start and end dates.',
                        'Enter the total metered volume in MWh.',
                        'For grid-connected autoproduction projects (Law 82-21), two extra fields appear: "Self-Consumption (MWh)" and "Grid Injected (MWh)". Their sum must equal the total.',
                        'Click "Certify Measurement Readings" to submit.',
                    ]
                },
                {
                    heading: 'Automatic Volume Validation',
                    body: 'The system automatically validates: Self-Consumption + Grid Injection = Total. If the sum does not match, submission is blocked.',
                    callout: { type: 'warning', text: 'Warning: A balance error causes automatic rejection of the certificate request.' }
                }
            ]
        },
        vvb: {
            title: 'VVB Guide — Independent Auditor',
            badge: 'ISO 14065 Accredited',
            sections: [
                {
                    heading: 'Scope of Intervention',
                    body: 'VVBs (Voluntary Verification Bodies) are activated for Direct Line projects (off national grid) and for autoproducers who choose an independent auditor for industrial confidentiality.'
                },
                {
                    heading: 'Your Assigned Assets',
                    body: 'On the Metering Portal, you only see assets explicitly assigned to you by the MTEDD. If an asset is missing, contact the administrator for assignment.'
                },
                {
                    heading: 'Verification Procedure',
                    steps: [
                        'Review the plant audit report.',
                        'Access the Metering Portal and select the plant.',
                        'Enter verified data (identical to ONEE procedure).',
                        'The certificate seal will automatically be "Verified by: Independent Auditor (VVB)".',
                    ]
                }
            ]
        },
        mitedd: {
            title: 'MTEDD Guide — Sovereign Authority',
            badge: 'Ministry / Admin',
            sections: [
                {
                    heading: 'Ministerial Dashboard',
                    body: 'As the Sovereign Authority, you have access to the "Command Center" view of the national registry: all assets, all certificates, all transactions — unfiltered.'
                },
                {
                    heading: 'Approval Queue (Page: /admin/approvals)',
                    body: 'This is the central interface for sovereign issuance. It displays:',
                    steps: [
                        'Certificate requests submitted by ONEE/VVB (status: PENDING_ISSUANCE).',
                        'Asset registration requests awaiting qualification.',
                        'Click "Mint GO" to create the cryptographic certificate.',
                        'Click "Qualify" to approve a production asset.',
                    ]
                },
                {
                    heading: 'Audit Log (Page: /admin/audit)',
                    body: 'All registry actions are recorded immutably. The log displays: actor, role, action, timestamp, and status of every operation.',
                    callout: { type: 'info', text: 'The audit log is the cornerstone of regulatory compliance for AIB and European body inspections.' }
                }
            ]
        },
        certificates: {
            title: 'Certificates, Transfers & Disclosures',
            badge: 'Certificate Lifecycle',
            sections: [
                {
                    heading: 'EECS Certificate Structure',
                    body: null,
                    table: {
                        headers: ['Field', 'Example Value', 'Meaning'],
                        rows: [
                            ['UUID', '4a81d3b3-...', 'Unique identifier in the registry'],
                            ['GS1 GIAI', '100-MA-0000000003', 'Standardized GS1 ID (Morocco = MA)'],
                            ['Serial Range', '1501 — 2500', 'Represents 1000 GOs (1 GO = 1 MWh)'],
                            ['Face Value', '1 MWh', 'Each certificate = 1 MWh'],
                            ['Category', 'SELF_CONSUMPTION / GRID_INJECTED', 'Law 82-21 Engine'],
                            ['RED II Expiry', '12 months post-production', 'Automatic transfer lock'],
                        ]
                    }
                },
                {
                    heading: 'Performing a Transfer (Page: /transfers/new)',
                    fields: [
                        { name: 'Source Certificate', hint: 'Select from your portfolio' },
                        { name: 'Recipient', hint: 'Email or ID of receiving entity' },
                        { name: 'Volume (MWh)', hint: 'Must be ≤ available volume' },
                    ]
                },
                {
                    heading: 'Cancel / Disclose (Page: /certificates)',
                    body: 'Click "Cancel" on a certificate to start the disclosure process. A bilingual (FR/EN) PDF will be automatically generated.',
                    callout: { type: 'warning', text: 'Disclosure is irreversible. Once cancelled, the certificate can no longer be transferred.' }
                }
            ]
        },
        red2: {
            title: 'RED II Compliance & EU Export',
            badge: 'Directive RED II (2018/2001)',
            sections: [
                {
                    heading: 'Transactional Validity Rules',
                    body: null,
                    table: {
                        headers: ['Rule', 'Deadline', 'Consequence'],
                        rows: [
                            ['Transfer Lock', '12 months after production end', 'Transfer automatically blocked'],
                            ['Disclosure Limit', '18 months after production end', 'Certificate auto-expired'],
                            ['Cryptographic Signature', 'At Minting', 'Immutable SHA-256 hash'],
                        ]
                    }
                },
                {
                    heading: 'Preparing an EU Export',
                    steps: [
                        'Ensure the certificate is less than 12 months old.',
                        'Download the certificate PDF (with QR code and SHA-256 hash).',
                        'The attestation mentions the verifying body (ONEE or VVB).',
                        'Submit the PDF to the European partner body (AIB Hub).',
                    ]
                }
            ]
        },
        h2: {
            title: 'Green Hydrogen — Law 82-21',
            badge: 'Autoproduction H2',
            sections: [
                {
                    heading: 'The 800/200 Splitting Engine',
                    body: 'For a green hydrogen project producing 1000 MWh, the platform automatically splits:'
                },
                {
                    heading: 'Full Example',
                    body: null,
                    table: {
                        headers: ['Parameter', 'Value', 'Resulting Badge'],
                        rows: [
                            ['Total Production', '1000 MWh', '—'],
                            ['H2 Self-Consumption', '800 MWh', 'Indigo Badge (SELF_CONSUMPTION)'],
                            ['Grid Injection', '200 MWh', 'Blue Badge (GRID_INJECTED)'],
                        ]
                    }
                },
                {
                    heading: 'Entry Procedure (ONEE / VVB)',
                    steps: [
                        'Select the H2 plant (it must be tagged "Autoproduction").',
                        'Enter 1000 in "Total Metered Output".',
                        'The form automatically shows "Self-Consumption" and "Grid Injected" fields.',
                        'Enter 800 and 200 respectively.',
                        'Submit — two distinct certificates are created.',
                    ],
                    callout: { type: 'info', text: 'Self-Consumption certificates are reserved for carbon reporting of exported hydrogen. Grid certificates can be sold on the national market.' }
                }
            ]
        },
        verify: {
            title: 'Public Verification (QR Code)',
            badge: 'Transparent & Sovereign',
            sections: [
                {
                    heading: 'How to Scan?',
                    steps: [
                        'Open the camera app on your smartphone.',
                        'Point it at the QR code on the certificate PDF.',
                        'Navigate to "go.mapcure.com/verify/[UUID]".',
                        'The screen shows the green verification seal with asset details.',
                    ]
                },
                {
                    heading: 'Information Displayed',
                    body: null,
                    table: {
                        headers: ['Information', 'Source'],
                        rows: [
                            ['Plant Name', 'MAP GO Registry'],
                            ['Verifying Body', 'ONEE or Accredited VVB'],
                            ['Category', 'SELF_CONSUMPTION / GRID_INJECTED'],
                            ['Status', 'ISSUED / CANCELLED / EXPIRED'],
                            ['SHA-256 Hash', 'Cryptographic Signature'],
                        ]
                    }
                },
                {
                    heading: 'No Account Required',
                    body: 'Public verification is accessible without login, ensuring transparency for international buyers and European auditors.',
                    callout: { type: 'info', text: 'This portal is the "Sovereign Window" open to the global green hydrogen market.' }
                }
            ]
        }
    }
};

/* ─────────────────────────────────────────────────────────── */
/*  COMPONENTS                                                 */
/* ─────────────────────────────────────────────────────────── */

const CALLOUT_STYLES = {
    warning: 'bg-amber-50 border-amber-400 text-amber-800',
    info: 'bg-blue-50 border-blue-400 text-blue-800',
};

function Section({ section }) {
    return (
        <div className="space-y-4">
            <h3 className="text-base font-black text-slate-900 uppercase tracking-tight border-l-4 border-[#155e75] pl-4">
                {section.heading}
            </h3>

            {section.body && (
                <p className="text-sm text-slate-600 leading-relaxed font-medium pl-4">{section.body}</p>
            )}

            {section.fields && (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3 pl-4">
                    {section.fields.map((f, i) => (
                        <div key={i} className="bg-slate-50 border border-slate-100 rounded-xl p-4">
                            <p className="text-[11px] font-black text-[#155e75] uppercase tracking-widest mb-1">{f.name}</p>
                            <p className="text-xs text-slate-500 font-medium">{f.hint}</p>
                        </div>
                    ))}
                </div>
            )}

            {section.steps && (
                <ol className="pl-4 space-y-2">
                    {section.steps.map((step, i) => (
                        <li key={i} className="flex items-start gap-3">
                            <span className="w-6 h-6 rounded-lg bg-[#155e75] text-white text-[10px] font-black flex items-center justify-center flex-shrink-0 mt-0.5">{i + 1}</span>
                            <span className="text-sm text-slate-600 font-medium leading-relaxed">{step}</span>
                        </li>
                    ))}
                </ol>
            )}

            {section.table && (
                <div className="pl-4 overflow-x-auto">
                    <table className="w-full text-xs border-collapse">
                        <thead>
                            <tr className="bg-slate-100">
                                {section.table.headers.map((h, i) => (
                                    <th key={i} className="text-left px-4 py-2 text-[10px] font-black text-slate-500 uppercase tracking-widest">{h}</th>
                                ))}
                            </tr>
                        </thead>
                        <tbody>
                            {section.table.rows.map((row, i) => (
                                <tr key={i} className="border-t border-slate-100 hover:bg-slate-50">
                                    {row.map((cell, j) => (
                                        <td key={j} className={`px-4 py-3 text-slate-700 font-medium ${j === 0 ? 'font-black text-slate-900' : ''}`}>{cell}</td>
                                    ))}
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            )}

            {section.callout && (
                <div className={`ml-4 border-l-4 p-4 rounded-r-xl text-xs font-medium leading-relaxed ${CALLOUT_STYLES[section.callout.type]}`}>
                    {section.callout.text}
                </div>
            )}
        </div>
    );
}

/* ─────────────────────────────────────────────────────────── */
/*  MAIN PAGE                                                  */
/* ─────────────────────────────────────────────────────────── */

export default function WikiHow() {
    const [sidebarOpen, setSidebarOpen] = useState(false);
    const [activePage, setActivePage] = useState('intro');
    const [lang, setLang] = useState(getLang);
    const mainRef = useRef(null);

    const selectPage = (pageId) => {
        setActivePage(pageId);
        setSidebarOpen(false);
    };

    useEffect(() => {
        if (mainRef.current) mainRef.current.scrollTop = 0;
    }, [activePage]);

    // Stay in sync when another page (or the sidebar toggle) changes the language
    useEffect(() => {
        const onStorage = (e) => {
            if (e.key === 'lang' && e.newValue) {
                setLang(e.newValue);
            }
        };
        window.addEventListener('storage', onStorage);
        return () => window.removeEventListener('storage', onStorage);
    }, []);

    const handleLangChange = (l) => {
        setGlobalLang(l);
        setLang(l);
    };

    const categories = CATEGORIES[lang];
    const page = PAGES[lang][activePage];

    return (
        <div className="flex h-full min-h-0 bg-slate-50 overflow-hidden" style={{ fontFamily: "'Inter', sans-serif" }}>

            {/* Mobile overlay — tap content area to close sidebar */}
            {sidebarOpen && (
                <button
                    type="button"
                    aria-label="Close menu"
                    className="fixed inset-0 z-40 bg-black/40 lg:hidden"
                    onClick={() => setSidebarOpen(false)}
                />
            )}

            {/* ── Sidebar ── */}
            <aside className={`${sidebarOpen ? 'translate-x-0' : '-translate-x-full'} fixed inset-y-0 left-0 z-50 w-72 bg-[#0e2d36] transition-transform duration-300 lg:relative lg:translate-x-0 flex flex-col shadow-2xl`}>

                {/* Sidebar Header */}
                <div className="px-6 py-5 border-b border-white/10 flex items-center justify-between">
                    <div className="flex items-center gap-3">
                        <div className="w-8 h-8 bg-white/10 rounded-lg flex items-center justify-center">
                            <BookOpenIcon className="w-5 h-5 text-white" />
                        </div>
                        <div>

                            <p className="text-[9px] text-white/40 font-bold uppercase tracking-widest">WikiHow</p>
                        </div>
                    </div>
                    <button onClick={() => setSidebarOpen(false)} className="lg:hidden text-white/60 hover:text-white">
                        <XMarkIcon className="w-5 h-5" />
                    </button>
                </div>

                {/* Language Toggle */}
                <div className="px-4 py-3 border-b border-white/10 flex gap-2">
                    <button
                        onClick={() => handleLangChange('fr')}
                        className={`flex-1 text-[10px] font-black py-2 rounded-lg uppercase tracking-widest transition-all ${lang === 'fr' ? 'bg-white text-[#0e2d36]' : 'text-white/40 hover:text-white'}`}
                    >
                        Français
                    </button>
                    <button
                        onClick={() => handleLangChange('en')}
                        className={`flex-1 text-[10px] font-black py-2 rounded-lg uppercase tracking-widest transition-all ${lang === 'en' ? 'bg-white text-[#0e2d36]' : 'text-white/40 hover:text-white'}`}
                    >
                        English
                    </button>
                </div>

                {/* Nav Items */}
                <nav className="flex-1 py-3 px-3 space-y-0.5 overflow-y-auto">
                    {categories.map(cat => {
                        const Icon = cat.icon;
                        const isActive = activePage === cat.id;
                        return (
                            <button
                                key={cat.id}
                                onClick={() => selectPage(cat.id)}
                                className={`w-full flex items-center gap-3 px-4 py-3 rounded-xl text-left transition-all ${isActive ? 'bg-white/15 text-white' : 'text-white/50 hover:bg-white/8 hover:text-white/80'}`}
                            >
                                <Icon className="w-4 h-4 flex-shrink-0" />
                                <span className="text-[11px] font-bold">{cat.label}</span>
                            </button>
                        );
                    })}
                </nav>

                {/* Back link */}
                <div className="px-6 pb-6 pt-3 border-t border-white/10">
                    <Link to="/" className="flex items-center gap-2 text-[10px] font-black text-white/40 hover:text-white uppercase tracking-widest transition-colors">
                        <ArrowLeftIcon className="w-3.5 h-3.5" />
                        {t('wiki_back_to_portal')}
                    </Link>
                </div>
            </aside>

            {/* ── Main Content ── */}
            <main className="flex-1 flex flex-col min-h-0 overflow-hidden">

                {/* Mobile toolbar — fixed above scroll area, always visible */}
                <header className="flex-shrink-0 h-14 bg-white border-b border-slate-200 px-4 flex items-center justify-between lg:hidden z-30">
                    <button type="button" onClick={() => setSidebarOpen(true)} className="p-2 rounded-lg bg-slate-50">
                        <Bars3Icon className="w-5 h-5 text-slate-600" />
                    </button>
                    <span className="text-xs font-black text-[#155e75] uppercase tracking-widest">MAP GO Wiki</span>
                    <div className="flex gap-1">
                        <button type="button" onClick={() => handleLangChange('fr')} className={`text-[9px] font-black px-2 py-1 rounded ${lang === 'fr' ? 'bg-[#155e75] text-white' : 'bg-slate-100 text-slate-500'}`}>FR</button>
                        <button type="button" onClick={() => handleLangChange('en')} className={`text-[9px] font-black px-2 py-1 rounded ${lang === 'en' ? 'bg-[#155e75] text-white' : 'bg-slate-100 text-slate-500'}`}>EN</button>
                    </div>
                </header>

                {/* Scrollable content */}
                <div ref={mainRef} className="flex-1 overflow-y-auto min-h-0">
                <div className="max-w-4xl mx-auto w-full px-6 py-10 md:px-12 md:py-16 lg:px-20 lg:py-20">
                    <div key={activePage + lang} className="animate-fade-in space-y-14">

                        {/* Page Header */}
                        <div className="pb-8 border-b border-slate-100 space-y-4">
                            <span className={`inline-flex items-center gap-2 px-3 py-1.5 bg-[#155e75]/10 text-[#155e75] text-[9px] font-black uppercase tracking-widest rounded-full`}>
                                <DocumentTextIcon className="w-3 h-3" /> {page.badge}
                            </span>
                            <h1 className="text-3xl md:text-4xl font-black text-slate-900 uppercase tracking-tight leading-tight">
                                {page.title}
                            </h1>
                        </div>

                        {/* Sections */}
                        <div className="space-y-12">
                            {page.sections.map((section, i) => (
                                <Section key={i} section={section} />
                            ))}
                        </div>

                        {/* Footer nav */}
                        <div className="pt-12 border-t border-slate-100 flex items-center justify-between">
                            <div className="flex items-center gap-2">
                                <CheckCircleIcon className="w-4 h-4 text-emerald-500" />
                                <span className="text-[10px] font-black text-slate-400 uppercase tracking-widest">
                                    {t('wiki_regulatory_cell')}
                                </span>
                            </div>
                            <span className="text-[10px] font-black text-slate-300 uppercase tracking-widest">May 2026 — v1.2</span>
                        </div>
                    </div>
                </div>
                </div>
            </main>
        </div>
    );
}
